import axios from "axios";
import { getCookie } from "cookies-next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaEye,
  FaPlus,
  FaTrash,
  FaUpload,
  FaXmark,
} from "react-icons/fa6";
import { Document, Page as PagePdf, pdfjs } from "react-pdf";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Query } from "@syncfusion/ej2-data";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import {
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  GridComponent,
  Inject,
  Page,
  Resize,
  Search,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";

import { FaEdit, FaSync } from "react-icons/fa";
import { Header, PageLoading } from "../../../components";
import CustomDraggable from "../../../components/CustomDraggable";
import { API_ROOT } from "../../../config";
import { useStateContext } from "../../../contexts/ContextProvider";
import formatDate from "../../../handlers/formatDate";
import "../../table.css";

import usePersistState from "../../../hooks/usePersistState";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import CustomTextDraggable from "../../../components/CustomTextDraggable";
import CustomTextInfoDraggable from "../../../components/CustomTextInfoDraggable";
import CustomQrRedirectDraggable from "../../../components/CustomQrRedirectDraggable";
import CustomSignatureRedirectDraggable from "../../../components/CustomSignatureRedirectDraggable";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FormSection = ({ title, children, className = "" }) => {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className}`}
    >
      <h3 className="mb-4 text-base font-semibold text-gray-700">{title}</h3>

      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
};

const SubCard = ({ title, children, className = "" }) => {
  return (
    <div
      className={`rounded-md border border-gray-300 bg-white p-3 ${className}`}
    >
      <h5 className="mb-3 text-sm font-semibold text-gray-600">{title}</h5>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
};

const SPECIMEN_MODES = [
  { text: "Markany", value: "markany" },
  { text: "Paperless", value: "paperless" },
];

const QR_REDIRECT_TYPES = [
  { text: "QR Code", value: "qr" },
  { text: "Signature Image", value: "signature" },
];

const SignatureSpecimenDetail = () => {
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  const { uuid } = useParams();

  const [isPreviewPdfMode, setIsPreviewPdfMode] = useState(true);
  const [pdfSize, setPdfSize] = useState(null);
  const pagesRef = useRef(null);
  const [qrImage, setQrImage] = useState(null);
  const [qrSize, setQrSize] = usePersistState(`_qrSize-${uuid}`, 75);
  const [qrPosition, setQrPosition] = usePersistState(`_qrPosition-${uuid}`, {
    x: 40,
    y: -40,
    lowerLeftX: 40,
    lowerLeftY: 40,
    upperRightX: 125,
    upperRightY: 125,
  });

  const [textPosition, setTextPosition] = usePersistState(
    `_textPosition-${uuid}`,
    {
      x: 40,
      y: -140,
    },
  );

  const [textInfoPosition, setTextInfoPosition] = usePersistState(
    `_textInfoPosition-${uuid}`,
    {
      x: 40,
      y: -20,
    },
  );
  const [textInfoBgOpacity, setTextInfoBgOpacity] = usePersistState(
    `_textInfoBgOpacity-${uuid}`,
    50,
  );

  const [qrRedirectPosition, setQrRedirectPosition] = usePersistState(
    `_qrRedirectPosition-${uuid}`,
    {
      x: 120,
      y: -70,
    },
  );
  const [qrRedirectSize, setQrRedirectSize] = usePersistState(
    `_qrRedirectSize-${uuid}`,
    50,
  );
  const [qrRedirectPageMode, setQrRedirectPageMode] = usePersistState(
    `_qrRedirectPageMode-${uuid}`,
    "all-page",
  );
  const [qrRedirectPage, setQrRedirectPage] = usePersistState(
    `_qrRedirectPage-${uuid}`,
    1,
  );
  const [qrRedirectCustomList, setQrRedirectCustomList] = usePersistState(
    `_qrRedirectCustomList-${uuid}`,
    [],
  );

  // ==== Tambahan: halaman yang dikecualikan saat mode "All Page (Except)" - hanya paperless ====
  const [qrRedirectExceptPages, setQrRedirectExceptPages] = usePersistState(
    `_qrRedirectExceptPages-${uuid}`,
    [],
  );
  const [exceptPageInput, setExceptPageInput] = useState("");

  const [pageLoading, setPageLoading] = useState(true);
  const [specimenDetail, setSpecimenDetail] = useState();
  const [coordinateSpecimenList, setCoordinateSpecimenList] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [toggleAdd, setToggleAdd] = useState(false);
  const [toggleEdit, setToggleEdit] = useState(false);
  const [toggleDetail, setToggleDetail] = useState(false);
  const [isDetailMode, setIsDetailMode] = useState(false);

  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);

  const onFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      if (uploadedFile.type !== "application/pdf") {
        toast.error("Only PDF files are allowed!");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setFile(uploadedFile);
        setPageNumber(1);
      };
      reader.readAsDataURL(uploadedFile);
    } else {
      setFile(null);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const generateCustomQrId = () =>
    `custom-qr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const [editCoordinateSpecimen, setEditCoordinateSpecimen] = useState({
    uuid: null,
    specimen_ttd_id: uuid,
    document_type_id: null,
    is_visual_sign: false,
    is_active: true,
    lower_left_x: 40,
    lower_left_y: 40,
    upper_right_x: 125,
    upper_right_y: 125,
    page: 1,
    certificate_level: "NOT_CERTIFIED",
    var_location: null,
    var_reason: null,
    textCoordinateX: 40,
    textCoordinateY: 160,
    qrRedirectCoordinateX: 120,
    qrRedirectCoordinateY: 70,
    qrRedirectSize: 50,
    qrRedirectPageMode: "all-page",
    qrRedirectPage: 1,
    qrRedirectExceptPages: [],
    isShowTextInfo: true,
    textInfoBgOpacity: 50,
    textInfoCoordinateX: 40,
    textInfoCoordinateY: 20,

    // Tambahan
    specimen_mode: "markany",
  });

  const [editCoordinateSpecimenWarning, setEditCoordinateSpecimenWarning] =
    useState({
      uuid: null,
      document_type_id: null,
      is_visual_sign: null,
      is_active: true,
      lower_left_x: null,
      lower_left_y: null,
      upper_right_x: null,
      upper_right_y: null,
      page: null,
      certificate_level: null,
      var_location: null,
      var_reason: null,
      textCoordinateX: null,
      textCoordinateY: null,
      qrRedirectCoordinateX: null,
      qrRedirectCoordinateY: null,
      qrRedirectSize: null,
      qrRedirectPageMode: null,
      qrRedirectPage: null,
      qrRedirectExceptPages: null,
      isShowTextInfo: null,
      textInfoBgOpacity: null,
      textInfoCoordinateX: null,
      textInfoCoordinateY: null,

      // Tambahan
      specimen_mode: null,
      qrRedirectCustom: null,
    });

  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  }, [pageNumber]);

  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      lower_left_x: qrPosition.lowerLeftX,
      lower_left_y: qrPosition.lowerLeftY,
      upper_right_x: qrPosition.upperRightX,
      upper_right_y: qrPosition.upperRightY,
    }));
  }, [qrPosition]);

  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      textCoordinateX: textPosition.x,
      textCoordinateY: Math.abs(textPosition.y),
    }));
  }, [textPosition]);

  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      textInfoCoordinateX: textInfoPosition.x,
      textInfoCoordinateY: Math.abs(textInfoPosition.y),
    }));
  }, [textInfoPosition]);

  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      qrRedirectCoordinateX: qrRedirectPosition.x,
      qrRedirectCoordinateY: Math.abs(qrRedirectPosition.y),
      qrRedirectSize: qrRedirectSize,
      qrRedirectPageMode: qrRedirectPageMode,
      qrRedirectPage: qrRedirectPage,
      textInfoBgOpacity: textInfoBgOpacity,
    }));
  }, [
    qrRedirectPosition.x,
    qrRedirectPosition.y,
    qrRedirectSize,
    qrRedirectPageMode,
    qrRedirectPage,
    textInfoBgOpacity,
  ]);

  // Tambahan: sinkron halaman dikecualikan (all-page-except, khusus paperless)
  useEffect(() => {
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      qrRedirectExceptPages: qrRedirectExceptPages,
    }));
  }, [qrRedirectExceptPages]);

  // Kalau mode paperless, is_visual_sign wajib false & tidak bisa diaktifkan
  useEffect(() => {
    if (editCoordinateSpecimen.specimen_mode === "paperless") {
      setEditCoordinateSpecimen((prev) =>
        prev.is_visual_sign ? { ...prev, is_visual_sign: false } : prev,
      );
    }
  }, [editCoordinateSpecimen.specimen_mode]);

  const selectedDocumentType = useMemo(() => {
    const selected = documentTypes.find(
      (d) => d?.id === editCoordinateSpecimen?.document_type_id,
    );
    return selected;
  }, [editCoordinateSpecimen.document_type_id, documentTypes]);

  const gridRef = useRef(null);

  const certificateLevels = [
    { text: "NOT CERTIFIED", value: "NOT_CERTIFIED" },
    { text: "NO CHANGE ALLOWED", value: "NO_CHANGE_ALLOWED" },
  ];

  const isPaperlessMode = editCoordinateSpecimen.specimen_mode === "paperless";

  // Page mode: markany persis seperti sekarang (2 opsi). Paperless dapat opsi ke-3.
  const qrPageModes = isPaperlessMode
    ? [
        { text: "All Page", value: "all-page" },
        { text: "Per Page", value: "per-page" },
        { text: "All Page (Except)", value: "all-page-except" },
        { text: "None", value: "none" },
      ]
    : [
        { text: "All Page", value: "all-page" },
        { text: "Per Page", value: "per-page" },
      ];

  const queryCellInfo = (args) => {
    args.cell?.classList.add("e-cell");
  };

  const actionTemplate = (props) => {
    return (
      <div className="e-actionParent flex gap-2">
        <button className="e-action" onClick={() => handleDetail(props)}>
          <FaEye className="e-actionIcon" />
          <div>Detail</div>
        </button>
        <button className="e-action" onClick={() => handleEdit(props)}>
          <FaEdit className="e-actionIcon" />
          <div>Edit</div>
        </button>
        <button
          className="e-action text-red-600"
          onClick={() => handleDelete(props)}
        >
          <FaTrash className="e-actionIcon" />
          <div>Delete</div>
        </button>
        <button
          className="flex items-center gap-2 py-2 px-3 border border-[#3b82f6] rounded-md text-[#3b82f6]"
          onClick={() => handleUploadMarkany(props)}
        >
          <FaUpload />
          <div>Upload PDF to Markany</div>
        </button>
      </div>
    );
  };

  const resetForm = () => {
    const defaultLLX = 40;
    const defaultLLY = 40;
    const defaultURX = 125;
    const defaultURY = 125;
    const defaultPage = 1;
    const defaultTextX = 40;
    const defaultTextY = 140;

    setFile(null);
    setEditCoordinateSpecimen({
      uuid: null,
      specimen_ttd_id: uuid,
      document_type_id: null,
      is_visual_sign: false,
      is_active: true,
      lower_left_x: defaultLLX,
      lower_left_y: defaultLLY,
      upper_right_x: defaultURX,
      upper_right_y: defaultURY,
      page: defaultPage,
      certificate_level: "NOT_CERTIFIED",
      var_location: null,
      var_reason: null,
      textCoordinateX: defaultTextX,
      textCoordinateY: defaultTextY,
      qrRedirectCoordinateX: 120,
      qrRedirectCoordinateY: 70,
      qrRedirectSize: 50,
      qrRedirectPageMode: "all-page",
      qrRedirectPage: 1,
      qrRedirectExceptPages: [],
      isShowTextInfo: true,
      textInfoBgOpacity: 50,
      textInfoCoordinateX: 40,
      textInfoCoordinateY: 20,

      specimen_mode: "markany",
    });
    setEditCoordinateSpecimenWarning({
      uuid: null,
      document_type_id: null,
      is_visual_sign: null,
      is_active: true,
      lower_left_x: null,
      lower_left_y: null,
      upper_right_x: null,
      upper_right_y: null,
      page: null,
      certificate_level: null,
      var_location: null,
      var_reason: null,
      textCoordinateX: null,
      textCoordinateY: null,
      qrRedirectCoordinateX: null,
      qrRedirectCoordinateY: null,
      qrRedirectSize: null,
      qrRedirectPageMode: null,
      qrRedirectPage: null,
      qrRedirectExceptPages: null,
      isShowTextInfo: null,
      textInfoBgOpacity: null,
      textInfoCoordinateX: null,
      textInfoCoordinateY: null,

      specimen_mode: null,
      qrRedirectCustom: null,
    });

    setQrPosition({
      x: defaultLLX,
      y: -40,
      lowerLeftX: defaultLLX,
      lowerLeftY: defaultLLY,
      upperRightX: defaultURX,
      upperRightY: defaultURY,
    });
    setTextPosition({
      x: defaultTextX,
      y: -defaultTextY,
    });
    setTextInfoPosition({
      x: 40,
      y: -20,
    });
    setQrRedirectPosition({
      x: 140,
      y: -70,
    });
    setQrRedirectSize(50);
    setQrRedirectPageMode("all-page");
    setQrRedirectPage(1);
    setQrRedirectCustomList([]);
    setQrRedirectExceptPages([]);
    setExceptPageInput("");
    setTextInfoBgOpacity(50);
    setPageNumber(defaultPage);
  };

  const handleCoordinateChange = (field, value) => {
    const parsed = Number(value);

    setEditCoordinateSpecimen((prev) => {
      let next = { ...prev, [field]: parsed };

      let lowerLeftX = Number(next.lower_left_x) || 0;
      let lowerLeftY = Number(next.lower_left_y) || 0;
      let upperRightX = Number(next.upper_right_x) || 0;
      let upperRightY = Number(next.upper_right_y) || 0;

      const width =
        (prev.upper_right_x ? Number(prev.upper_right_x) : 95) -
        (prev.lower_left_x ? Number(prev.lower_left_x) : 20);
      const height =
        (prev.upper_right_y ? Number(prev.upper_right_y) : 95) -
        (prev.lower_left_y ? Number(prev.lower_left_y) : 20);

      if (field === "lower_left_x") {
        upperRightX = lowerLeftX + width;
      }
      if (field === "lower_left_y") {
        upperRightY = lowerLeftY + height;
      }

      if (field === "upper_right_x") {
        lowerLeftX = upperRightX - width;
      }
      if (field === "upper_right_y") {
        lowerLeftY = upperRightY - height;
      }

      next = {
        ...next,
        lower_left_x: lowerLeftX,
        lower_left_y: lowerLeftY,
        upper_right_x: upperRightX,
        upper_right_y: upperRightY,
      };

      setQrPosition({
        x: lowerLeftX,
        y: -lowerLeftY,
        lowerLeftX,
        lowerLeftY,
        upperRightX,
        upperRightY,
      });

      return next;
    });

    if (!value || String(value).length < 1) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: `${field.replace(/_/g, " ")} is required`,
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTextCoordinateChange = (field, value) => {
    const parsed = Number(value);

    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      [field]: parsed,
    }));

    if (field === "textCoordinateX") {
      setTextPosition((prev) => ({
        ...prev,
        x: parsed,
      }));
    } else if (field === "textCoordinateY") {
      setTextPosition((prev) => ({
        ...prev,
        y: -parsed,
      }));
    }

    if (!value || String(value).length < 1) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: `${field.replace(/_/g, " ")} is required`,
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTextInfoCoordinateChange = (field, value) => {
    const parsed = Number(value);

    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      [field]: parsed,
    }));

    if (field === "textInfoCoordinateX") {
      setTextInfoPosition((prev) => ({
        ...prev,
        x: parsed,
      }));
    } else if (field === "textInfoCoordinateY") {
      setTextInfoPosition((prev) => ({
        ...prev,
        y: -parsed,
      }));
    }

    if (!value || String(value).length < 1) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: `${field.replace(/_/g, " ")} is required`,
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleQrRedirectCoordinateChange = (field, value) => {
    const parsed = Number(value);

    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      [field]: parsed,
    }));

    if (field === "qrRedirectCoordinateX") {
      setQrRedirectPosition((prev) => ({
        ...prev,
        x: parsed,
      }));
    } else if (field === "qrRedirectCoordinateY") {
      setQrRedirectPosition((prev) => ({
        ...prev,
        y: -parsed,
      }));
    }

    if (!value || String(value).trim().length === 0) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: `${field.replace(/([A-Z])/g, " $1")} is required`,
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleQrRedirectSizeChange = (value) => {
    const parsed = Number(value);
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      qrRedirectSize: parsed,
    }));
    setQrRedirectSize(parsed);

    if (!value || parsed < 10 || parsed > 200) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        qrRedirectSize: "QR Redirect Size harus antara 10 - 200",
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        qrRedirectSize: null,
      }));
    }
  };

  const handleQrRedirectPageModeChange = (value) => {
    setQrRedirectPageMode(value);
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      qrRedirectPageMode: value,
    }));
  };

  const handleQrRedirectPageChange = (value) => {
    const parsed = Number(value);
    setQrRedirectPage(parsed);
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      qrRedirectPage: parsed,
    }));

    if (parsed < 1 || parsed > numPages) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        qrRedirectPage: `Page harus antara 1 - ${numPages}`,
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        qrRedirectPage: null,
      }));
    }
  };

  // ==== Tambahan: Specimen Mode (tab) ====
  const handleSpecimenModeChange = (value) => {
    if (isDetailMode) return;

    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      specimen_mode: value,
      ...(value === "paperless"
        ? { is_visual_sign: false, qrRedirectPageMode: "none" }
        : {}),
      ...((value === "markany" &&
        prev.qrRedirectPageMode === "all-page-except") ||
      prev.qrRedirectPageMode === "none"
        ? { qrRedirectPageMode: "all-page" }
        : {}),
    }));

    if (value === "markany" && qrRedirectPageMode === "all-page-except") {
      setQrRedirectPageMode("all-page");
    }

    if (value === "paperless") {
      setTextPosition({
        x: 0,
        y: 0,
      });
      setEditCoordinateSpecimen((prev) => ({
        ...prev,
        textCoordinateX: 0,
        textCoordinateY: 0,
      }));
    }
  };

  const handleIsVisualSignToggle = () => {
    if (editCoordinateSpecimen.specimen_mode === "paperless") return;
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      is_visual_sign: !prev.is_visual_sign,
    }));
  };

  // ==== Tambahan: Halaman dikecualikan (all-page-except, khusus paperless) ====
  const handleAddExceptPage = () => {
    const parsed = parseInt(exceptPageInput, 10);
    if (!parsed || parsed < 1 || (numPages && parsed > numPages)) {
      toast.error(`Halaman harus antara 1 - ${numPages || 1}`);
      return;
    }
    setQrRedirectExceptPages((prev) =>
      prev.includes(parsed) ? prev : [...prev, parsed].sort((a, b) => a - b),
    );
    setExceptPageInput("");
  };

  const handleRemoveExceptPage = (page) => {
    setQrRedirectExceptPages((prev) => prev.filter((p) => p !== page));
  };

  const handleAddCustomQr = () => {
    setQrRedirectCustomList((prev) => [
      ...prev,
      {
        id: generateCustomQrId(),
        page: pageNumber,
        x: 10,
        y: -40,
        size: 50,
        type: "qr",
        showSignatureInfo: false,
        signatureInfoX: 10,
        signatureInfoY: -10,
      },
    ]);
  };

  const handleRemoveCustomQr = (id) => {
    setQrRedirectCustomList((prev) => prev.filter((item) => item.id !== id));
  };

  const handleCustomQrFieldChange = (id, field, value) => {
    const parsed = Number(value);

    setQrRedirectCustomList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: field === "y" ? -Math.abs(parsed) : parsed,
            }
          : item,
      ),
    );
  };

  const handleCustomQrPositionChange = (id, position) => {
    setQrRedirectCustomList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, x: position.x, y: position.y } : item,
      ),
    );
  };

  const handleCustomQrSizeChange = (id, size) => {
    setQrRedirectCustomList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, size } : item)),
    );
  };

  const handleCustomQrTypeChange = (id, type) => {
    setQrRedirectCustomList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, type } : item)),
    );
  };

  const handleCustomQrShowSignatureInfoToggle = (id) => {
    setQrRedirectCustomList((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, showSignatureInfo: !item.showSignatureInfo }
          : item,
      ),
    );
  };

  const handleCustomQrSignatureInfoFieldChange = (id, field, value) => {
    const parsed = Number(value);
    setQrRedirectCustomList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: field === "signatureInfoY" ? Math.abs(parsed) : parsed,
            }
          : item,
      ),
    );
  };

  const handleCustomQrSignatureInfoPositionChange = (id, position) => {
    setQrRedirectCustomList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              signatureInfoX: position.x,
              signatureInfoY: Math.abs(position.y),
            }
          : item,
      ),
    );
  };

  const handleTextInfoBgOpacityChange = (value) => {
    const parsed = Number(value);
    setTextInfoBgOpacity(parsed);
    setEditCoordinateSpecimen((prev) => ({
      ...prev,
      textInfoBgOpacity: parsed,
    }));

    if (parsed < 0 || parsed > 100) {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        textInfoBgOpacity: "Opacity harus antara 0 - 100",
      }));
    } else {
      setEditCoordinateSpecimenWarning((prev) => ({
        ...prev,
        textInfoBgOpacity: null,
      }));
    }
  };

  const handleAdd = () => {
    resetForm();
    setIsDetailMode(false);
    setToggleAdd(true);
  };

  const handleDetail = async (data) => {
    setIsDetailMode(true);
    await fetchCoordinateDetail(data.Uuid);
    setToggleDetail(true);
  };

  const handleEdit = async (data) => {
    setIsDetailMode(false);
    await fetchCoordinateDetail(data.Uuid);
    setToggleEdit(true);
  };

  const handleDelete = async (data) => {
    setLoading(true);
    setLoadingAction("delete");
    await deleteCoordinateDetail(data.Uuid);
    setLoading(false);
    setLoadingAction(null);
    setRefresh((prev) => prev + 1);
  };

  const handleUploadMarkany = async (data) => {
    setLoading(true);
    setLoadingAction("upload");
    await uploadPdfMarkany(data.Uuid);
    setLoading(false);
    setLoadingAction(null);
    setRefresh((prev) => prev + 1);
  };

  const handleSync = async () => {
    setLoading(true);
    setLoadingAction("sync");

    try {
      const response = await axios.post(
        `${API_ROOT}/admin/specimen-signature/detail/${editCoordinateSpecimen.specimen_ttd_id}/coordinate/sync`,
        {},
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      toast.success(response.data?.message || "Coordinate Specimen Synced!");
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to sync specimen coordinate",
      );
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
    setLoadingAction(null);
  };

  function onDocumentTypeFiltering(args) {
    let query = new Query();
    query =
      args.text !== ""
        ? query.where("text", "contains", args.text, true)
        : query;
    args.updateData(documentTypes, query);
  }

  const dataBound = () => {
    if (gridRef.current) {
      gridRef.current.autoFitColumns([
        "No",
        "VarLocation",
        "VarReason",
        "MembershipName",
        "DocType",
        "KeyDocType",
        "CertificateLevel",
        "CreatedAt",
        "Uuid",
      ]);
    }
  };

  const toolbarClick = (args) => {
    if (gridRef.current && args.item.id.includes("excelexport")) {
      const excelExportProperties = {
        fileName: "Paperless - Coordinate Signature List.xlsx",
      };
      gridRef.current.excelExport(excelExportProperties);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const warnings = { ...editCoordinateSpecimenWarning };

    if (!editCoordinateSpecimen.document_type_id) {
      warnings.document_type_id = "Document Type is required";
      isValid = false;
    } else {
      warnings.document_type_id = null;
    }

    if (!editCoordinateSpecimen.specimen_mode) {
      warnings.specimen_mode = "Specimen Mode is required";
      isValid = false;
    } else {
      warnings.specimen_mode = null;
    }

    if (typeof editCoordinateSpecimen.is_visual_sign !== "boolean") {
      warnings.is_visual_sign = "Is Visual Sign format must be boolean";
      isValid = false;
    } else if (isPaperlessMode && editCoordinateSpecimen.is_visual_sign) {
      warnings.is_visual_sign =
        "Is Visual Sign harus false pada mode Paperless";
      isValid = false;
    } else {
      warnings.is_visual_sign = null;
    }

    const checkCoordinate = (value, name) => {
      if (
        value === null ||
        value === undefined ||
        String(value).trim().length === 0
      ) {
        isValid = false;
        return `${name} is required`;
      }
      return null;
    };

    if (!isPaperlessMode) {
      warnings.lower_left_x = checkCoordinate(
        editCoordinateSpecimen.lower_left_x,
        "Lower Left X",
      );
      warnings.lower_left_y = checkCoordinate(
        editCoordinateSpecimen.lower_left_y,
        "Lower Left Y",
      );
      warnings.upper_right_x = checkCoordinate(
        editCoordinateSpecimen.upper_right_x,
        "Upper Right X",
      );
      warnings.upper_right_y = checkCoordinate(
        editCoordinateSpecimen.upper_right_y,
        "Upper Right Y",
      );
      warnings.textCoordinateX = checkCoordinate(
        editCoordinateSpecimen.textCoordinateX,
        "Text Coordinate X",
      );
      warnings.textCoordinateY = checkCoordinate(
        editCoordinateSpecimen.textCoordinateY,
        "Text Coordinate Y",
      );
    } else {
      warnings.lower_left_x = null;
      warnings.lower_left_y = null;
      warnings.upper_right_x = null;
      warnings.upper_right_y = null;
      warnings.textCoordinateX = null;
      warnings.textCoordinateY = null;
    }

    if (
      editCoordinateSpecimen.page === null ||
      editCoordinateSpecimen.page === undefined ||
      String(editCoordinateSpecimen.page).trim().length === 0 ||
      parseInt(editCoordinateSpecimen.page) < 1
    ) {
      warnings.page = "Page is required and must be a positive number";
      isValid = false;
    } else {
      warnings.page = null;
    }

    if (
      !editCoordinateSpecimen.var_location ||
      editCoordinateSpecimen.var_location.trim().length < 1
    ) {
      warnings.var_location = "Location is required";
      isValid = false;
    } else {
      warnings.var_location = null;
    }

    if (
      !editCoordinateSpecimen.var_reason ||
      editCoordinateSpecimen.var_reason.trim().length < 1
    ) {
      warnings.var_reason = "Reason is required";
      isValid = false;
    } else {
      warnings.var_reason = null;
    }

    if (!editCoordinateSpecimen.certificate_level) {
      warnings.certificate_level = "Certificate Level is required";
      isValid = false;
    } else if (
      !certificateLevels.find(
        (certificateLevel) =>
          certificateLevel.value === editCoordinateSpecimen.certificate_level,
      )
    ) {
      warnings.certificate_level = "Selected Certificate Level is not correct";
      isValid = false;
    } else {
      warnings.certificate_level = null;
    }

    if (editCoordinateSpecimen.qrRedirectPageMode === "per-page") {
      if (
        editCoordinateSpecimen.qrRedirectPage < 1 ||
        editCoordinateSpecimen.qrRedirectPage > numPages
      ) {
        warnings.qrRedirectPage = `Page harus antara 1 - ${numPages}`;
        isValid = false;
      } else {
        warnings.qrRedirectPage = null;
      }

      const invalidCustomQr = qrRedirectCustomList.some(
        (item) =>
          !item.page ||
          item.page < 1 ||
          item.page > numPages ||
          item.size === undefined ||
          item.size < 10 ||
          item.size > 200,
      );
      if (invalidCustomQr) {
        warnings.qrRedirectCustom = `Setiap posisi custom harus memiliki page 1-${numPages}, size 10-200`;
        isValid = false;
      } else {
        warnings.qrRedirectCustom = null;
      }
    }

    // Tambahan: validasi Halaman Dikecualikan (khusus paperless, mode all-page-except)
    if (
      isPaperlessMode &&
      editCoordinateSpecimen.qrRedirectPageMode === "all-page-except"
    ) {
      if (!qrRedirectExceptPages || qrRedirectExceptPages.length === 0) {
        warnings.qrRedirectExceptPages =
          "Minimal 1 halaman pengecualian harus diisi";
        isValid = false;
      } else if (
        qrRedirectExceptPages.some(
          (p) => !p || p < 1 || (numPages && p > numPages),
        )
      ) {
        warnings.qrRedirectExceptPages = `Halaman pengecualian harus antara 1 - ${numPages || 1}`;
        isValid = false;
      } else {
        warnings.qrRedirectExceptPages = null;
      }
    } else {
      warnings.qrRedirectExceptPages = null;
    }

    if (
      editCoordinateSpecimen.textInfoBgOpacity < 0 ||
      editCoordinateSpecimen.textInfoBgOpacity > 100
    ) {
      warnings.textInfoBgOpacity = "Opacity harus antara 0 - 100";
      isValid = false;
    } else {
      warnings.textInfoBgOpacity = null;
    }

    setEditCoordinateSpecimenWarning(warnings);
    return isValid;
  };

  const handleSubmit = async (isEdit = false) => {
    if (!validateForm()) return;

    setLoading(true);
    setLoadingAction(isEdit ? "edit" : "add");

    try {
      const formData = new FormData();
      formData.append(
        "document_type_id",
        editCoordinateSpecimen.document_type_id,
      );
      formData.append("specimen_mode", editCoordinateSpecimen.specimen_mode);
      formData.append(
        "is_visual_sign",
        editCoordinateSpecimen.is_visual_sign ? "YES" : "NO",
      );
      formData.append(
        "is_active",
        editCoordinateSpecimen.is_active ? "true" : "false",
      );
      formData.append(
        "lower_left_x",
        parseInt(editCoordinateSpecimen.lower_left_x),
      );
      formData.append(
        "lower_left_y",
        parseInt(editCoordinateSpecimen.lower_left_y),
      );
      formData.append(
        "upper_right_x",
        parseInt(editCoordinateSpecimen.upper_right_x),
      );
      formData.append(
        "upper_right_y",
        parseInt(editCoordinateSpecimen.upper_right_y),
      );
      formData.append("page", parseInt(editCoordinateSpecimen.page));
      formData.append(
        "certificate_level",
        editCoordinateSpecimen.certificate_level,
      );
      formData.append("var_location", editCoordinateSpecimen.var_location);
      formData.append("var_reason", editCoordinateSpecimen.var_reason);
      formData.append(
        "textCoordinateX",
        parseInt(editCoordinateSpecimen.textCoordinateX),
      );
      formData.append(
        "textCoordinateY",
        parseInt(editCoordinateSpecimen.textCoordinateY + 35),
      );
      formData.append(
        "textInfoCoordinateX",
        parseInt(textInfoPosition.x || "0"),
      );
      formData.append(
        "textInfoCoordinateY",
        parseInt(-textInfoPosition.y || "0"),
      );
      formData.append(
        "qrRedirectCoordinateX",
        parseInt(editCoordinateSpecimen.qrRedirectCoordinateX || "0"),
      );
      formData.append(
        "qrRedirectCoordinateY",
        parseInt(editCoordinateSpecimen.qrRedirectCoordinateY || "0"),
      );
      formData.append(
        "qrRedirectSize",
        parseInt(editCoordinateSpecimen.qrRedirectSize || "0"),
      );
      formData.append(
        "qrRedirectPageMode",
        editCoordinateSpecimen.qrRedirectPageMode,
      );
      formData.append("qrRedirectPage", editCoordinateSpecimen.qrRedirectPage);

      // Tambahan: halaman dikecualikan (hanya terisi saat paperless + all-page-except)
      formData.append(
        "qrRedirectExceptPages",
        JSON.stringify(
          isPaperlessMode &&
            editCoordinateSpecimen.qrRedirectPageMode === "all-page-except"
            ? qrRedirectExceptPages
            : [],
        ),
      );

      formData.append(
        "qrRedirectCustom",
        JSON.stringify(
          qrRedirectCustomList.map((item) => {
            const base = {
              type: "qr",
              page: parseInt(item.page),
              x: parseInt(item.x),
              y: Math.abs(parseInt(item.y)),
              size: parseInt(item.size),
              showSignatureInfo: false,
              signatureInfoX: 0,
              signatureInfoY: 0,
            };

            if (!isPaperlessMode) return base;

            return {
              ...base,
              type: item.type || "qr",
              showSignatureInfo: !!item.showSignatureInfo,
              signatureInfoX: parseInt(item.signatureInfoX ?? 10),
              signatureInfoY: Math.abs(parseInt(item.signatureInfoY ?? 10)),
            };
          }),
        ),
      );
      formData.append(
        "textInfoBgOpacity",
        editCoordinateSpecimen.textInfoBgOpacity,
      );
      formData.append("isShowTextInfo", editCoordinateSpecimen.isShowTextInfo);

      if (file && typeof file !== "string") {
        formData.append("document_path", file);
      }

      if (isEdit) {
        await axios.put(
          `${API_ROOT}/admin/specimen-signature/detail/${editCoordinateSpecimen.specimen_ttd_id}/coordinate/update/${editCoordinateSpecimen.uuid}`,
          formData,
          {
            headers: {
              Authorization: getCookie("admin_auth"),
              "Content-Type": "multipart/form-data",
            },
          },
        );
        toast.success("Coordinate Updated!");
      } else {
        await axios.post(
          `${API_ROOT}/admin/specimen-signature/detail/${editCoordinateSpecimen.specimen_ttd_id}/coordinate/create`,
          formData,
          {
            headers: {
              Authorization: getCookie("admin_auth"),
              "Content-Type": "multipart/form-data",
            },
          },
        );
        toast.success("Coordinate Created!");
      }

      setToggleAdd(false);
      setToggleEdit(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.message ?? "Server Error");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
    setLoadingAction(null);
  };

  const fetchSpecimenDetail = async () => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/specimen-signature/detail/" + uuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      fetchMembershipTypesByIntegratedPlatform(
        response.data?.data?.integrated_member?.integrated_platform?.uuid,
      );
      setSpecimenDetail(response.data?.data);
    } catch (error) {
      console.log("Failed to fetch specimen TTD list:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchMembershipTypesByIntegratedPlatform = async (
    integratedPlatformId,
  ) => {
    try {
      const membershipResponse = await axios.get(
        API_ROOT +
          `/admin/select/membership-by-integrated-platform/${integratedPlatformId}`,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      setMembershipTypes(
        membershipResponse.data.data.map((item) => ({
          text: item.name,
          value: item.membershipTypeId,
        })),
      );
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchDocumentTypesByMembershipType = async (membershipTypeId) => {
    try {
      const response = await axios.get(
        API_ROOT +
          `/admin/select/document-types-by-membership/${membershipTypeId}`,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      setDocumentTypes(
        response.data.data.map((item) => ({
          text: item.name_doc,
          value: item.docTypeId,
          // dipakai untuk mencocokkan selectedDocumentType di atas
          id: item.docTypeId,
          // dipakai untuk menampilkan Orientation di "Document Type Selected"
          orientation: item.orientation,
        })),
      );
    } catch (error) {
      console.log("Failed to fetch document types:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchSpecimenDetailList = async () => {
    try {
      const response = await axios.get(
        API_ROOT +
          "/admin/specimen-signature/detail/" +
          uuid +
          "/coordinate/list",
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      const data = response.data.data;

      setQrImage(data.specimen_signature.filepath);

      setCoordinateSpecimenList(() =>
        data.coordinate_specimens.map((item, index) => ({
          No: index + 1,
          Uuid: item.coordinateSpecimenId,
          VarLocation: item.var_location,
          VarReason: item.var_reason,
          MembershipName: item.document_type?.membership_type?.name,
          DocType: item.document_type.name_doc,
          KeyDocType: item.document_type?.key_doc,
          CertificateLevel: item.certificate_level,
          CreatedAt: formatDate(item.created_at),
          IsActive: item.is_active,
          SpecimenMode: item.specimen_mode || "markany",
        })),
      );
      setPageLoading(false);
    } catch (error) {
      toast.error("Failed to fetch specimen details");
      console.log(error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
      setPageLoading(false);
    }
  };

  const fetchCoordinateDetail = async (coordinateUuid) => {
    try {
      setIsPreviewPdfMode(false);

      const response = await axios.get(
        API_ROOT +
          "/admin/specimen-signature/detail/" +
          uuid +
          "/coordinate/detail/" +
          coordinateUuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );

      const data = response.data.data;

      setEditCoordinateSpecimen({
        uuid: data.coordinateSpecimenId,
        specimen_ttd_id: uuid,
        document_type_id: data.document_type.docTypeId,
        is_visual_sign: data.is_visual_sign === "YES" ? true : false,
        is_active: data.is_active,
        lower_left_x: data.lower_left_x,
        lower_left_y: data.lower_left_y,
        upper_right_x: data.upper_left_x,
        upper_right_y: data.upper_left_y,
        page: data.page,
        certificate_level: data.certificate_level,
        var_location: data.var_location,
        var_reason: data.var_reason,
        textCoordinateX: data.textCoordinateX || 20,
        textCoordinateY: data.textCoordinateY || 120,
        membership_type_id:
          data.document_type?.membership_type?.membershipTypeId,
        qrRedirectCoordinateX: data.qrRedirectCoordinateX || 10,
        qrRedirectCoordinateY: data.qrRedirectCoordinateY || 40,
        qrRedirectSize: data.qrRedirectSize || 50,
        qrRedirectPageMode: data.qrRedirectPageMode || "all-page",
        qrRedirectPage: data.qrRedirectPage || 1,
        qrRedirectExceptPages: data.qrRedirectExceptPages || [],
        isShowTextInfo: data.isShowTextInfo ?? true,
        textInfoCoordinateX: data.textInfoCoordinateX || 10,
        textInfoCoordinateY: data.textInfoCoordinateY || 10,
        textInfoBgOpacity: data.textInfoBgOpacity || 50,

        // Tambahan
        specimen_mode: data.specimen_mode || "markany",
      });

      if (data.document_type?.membership_type?.membershipTypeId) {
        fetchDocumentTypesByMembershipType(
          data.document_type?.membership_type?.membershipTypeId,
        );
      }

      if (data?.document_path) {
        const base64String = data.document_path;

        const [metadata, base64Data] = base64String.split(",");

        const mime = metadata.match(/:(.*?);/)[1];

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: mime });

        setFile(blob);
      } else {
        setFile(null);
      }

      setQrPosition((prev) => ({
        ...prev,
        x: data.lower_left_x,
        y: data.lower_left_y * -1,
        lowerLeftX: data.lower_left_x,
        lowerLeftY: data.lower_left_y,
        upperRightX: data.upper_left_x,
        upperRightY: data.upper_left_y,
      }));

      setTextPosition({
        x: data.textCoordinateX || 20,
        y: -(data.textCoordinateY - 35 || 120),
      });

      setTextInfoPosition({
        x: data.textInfoCoordinateX,
        y: -data.textInfoCoordinateY,
      });

      setQrRedirectPosition({
        x: data.qrRedirectCoordinateX,
        y: -data.qrRedirectCoordinateY,
      });
      setQrRedirectSize(data.qrRedirectSize);
      setQrRedirectPageMode(data.qrRedirectPageMode || "all-page");
      setQrRedirectPage(data.qrRedirectPage || 1);
      setQrRedirectExceptPages(data.qrRedirectExceptPages || []);
      setTextInfoBgOpacity(data.textInfoBgOpacity || 50);

      if (data.qrRedirectCustom) {
        try {
          const parsedCustomQr =
            typeof data.qrRedirectCustom === "string"
              ? JSON.parse(data.qrRedirectCustom)
              : data.qrRedirectCustom;

          setQrRedirectCustomList(
            (parsedCustomQr || []).map((item) => ({
              id: generateCustomQrId(),
              page: item.page,
              x: item.x,
              y: -Math.abs(item.y),
              size: item.size,
              type: item.type || "qr",
              showSignatureInfo: !!item.showSignatureInfo,
              signatureInfoX: item.signatureInfoX ?? 10,
              signatureInfoY: Math.abs(item.signatureInfoY ?? 10),
            })),
          );
        } catch (e) {
          setQrRedirectCustomList([]);
        }
      } else {
        setQrRedirectCustomList([]);
      }

      setPageNumber(data.page);
    } catch (error) {
      toast.error("Failed to fetch coordinate details");
      console.log(error);
    }
  };

  const deleteCoordinateDetail = async (coordinateUuid) => {
    try {
      await axios.delete(
        API_ROOT +
          "/admin/specimen-signature/detail/" +
          uuid +
          "/coordinate/delete/" +
          coordinateUuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      toast.success("Success to delete coordinate details");
    } catch (error) {
      toast.error("Failed to delete coordinate details");
      console.log(error);
    }
  };

  const uploadPdfMarkany = async (coordinateUuid) => {
    try {
      const reponse = await axios.post(
        API_ROOT +
          "/admin/specimen-signature/detail/" +
          uuid +
          "/coordinate/upload-pdf-markany/" +
          coordinateUuid,
        {},
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      toast.success(
        reponse.data?.message || "Success to upload PDF to Markany",
      );
    } catch (error) {
      toast.error(
        error.response.data?.message || "Failed to upload PDF to Markany",
      );
      console.log(error);
    }
  };

  useEffect(() => {
    setPageLoading(true);
    fetchSpecimenDetail();
    fetchSpecimenDetailList();
  }, [refresh]);

  const renderFormFields = () => (
    <div className="flex flex-col gap-4 w-full">
      <ul className="flex flex-nowrap gap-2 text-sm bg-gray-100 p-1 rounded-md mt-2">
        {SPECIMEN_MODES.map((mode) => (
          <li
            key={mode.value}
            onClick={() => handleSpecimenModeChange(mode.value)}
            className={`w-1/2 px-4 py-2 rounded-md cursor-pointer text-center text-lg font-semibold ${
              editCoordinateSpecimen.specimen_mode === mode.value
                ? "bg-sky-400 text-white"
                : "bg-transparent text-gray-600"
            }`}
          >
            {mode.text}
          </li>
        ))}
      </ul>
      <FormSection title="General Information">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Is Visual Sign */}
          {!isPaperlessMode && (
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Is Visual Sign *</h4>
              <input
                type="checkbox"
                checked={editCoordinateSpecimen.is_visual_sign}
                onChange={handleIsVisualSignToggle}
                disabled={isDetailMode || isPaperlessMode}
                className="p-2 rounded-[8px] w-[16px] h-[16px]"
              />
            </div>
          )}

          {/* Membership Type */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Membership Type *</h4>

            <DropDownListComponent
              dataSource={membershipTypes}
              fields={{ text: "text", value: "value" }}
              placeholder="Select Membership Type"
              value={editCoordinateSpecimen.membership_type_id || ""}
              change={(e) => {
                if (e.value) {
                  fetchDocumentTypesByMembershipType(e.value);

                  setEditCoordinateSpecimen((prev) => ({
                    ...prev,
                    membership_type_id: e.value,
                  }));
                }
              }}
              allowFiltering
              filtering={onDocumentTypeFiltering.bind(this)}
              enabled={!isDetailMode}
              cssClass={
                editCoordinateSpecimenWarning.membership_type_id
                  ? "e-error"
                  : ""
              }
            />

            {editCoordinateSpecimenWarning.membership_type_id && (
              <p className="text-xs text-red-500">
                {editCoordinateSpecimenWarning.membership_type_id}
              </p>
            )}
          </div>

          {/* Document Type */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Document Type *</h4>

            <DropDownListComponent
              dataSource={documentTypes}
              fields={{ text: "text", value: "value" }}
              placeholder="Select Document Type"
              value={editCoordinateSpecimen.document_type_id || ""}
              change={(e) =>
                setEditCoordinateSpecimen((prev) => ({
                  ...prev,
                  document_type_id: e.value,
                }))
              }
              allowFiltering
              filtering={onDocumentTypeFiltering.bind(this)}
              enabled={
                !isDetailMode && !!editCoordinateSpecimen.membership_type_id
              }
              cssClass={
                editCoordinateSpecimenWarning.document_type_id ? "e-error" : ""
              }
            />

            {editCoordinateSpecimenWarning.document_type_id && (
              <p className="text-xs text-red-500">
                {editCoordinateSpecimenWarning.document_type_id}
              </p>
            )}
          </div>

          {/* Certificate Level */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Certificate Level *</h4>

            <DropDownListComponent
              dataSource={certificateLevels}
              fields={{ text: "text", value: "value" }}
              placeholder="Select Certificate Level"
              value={editCoordinateSpecimen.certificate_level || ""}
              change={(e) =>
                setEditCoordinateSpecimen((prev) => ({
                  ...prev,
                  certificate_level: e.value,
                }))
              }
              enabled={!isDetailMode}
            />
          </div>

          {/* Location */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Location *</h4>

            <input
              type="text"
              placeholder="Input location..."
              maxLength={100}
              spellCheck="false"
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              value={editCoordinateSpecimen.var_location || ""}
              disabled={isDetailMode}
              onChange={(e) =>
                setEditCoordinateSpecimen((prev) => ({
                  ...prev,
                  var_location: e.target.value,
                }))
              }
            />
          </div>
        </div>

        {/* Reason */}
        <div className="flex flex-col gap-2">
          <h4 className="font-medium">Reason *</h4>

          <textarea
            placeholder="Input reason..."
            maxLength={500}
            spellCheck="false"
            className="w-full p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            value={editCoordinateSpecimen.var_reason || ""}
            disabled={isDetailMode}
            onChange={(e) =>
              setEditCoordinateSpecimen((prev) => ({
                ...prev,
                var_reason: e.target.value,
              }))
            }
          />
        </div>
      </FormSection>
      {/* Signature Coordinate */}
      {!isPaperlessMode && (
        <FormSection title="Signature Coordinate">
          {/* Page */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Page *</h4>

            <input
              type="number"
              min="1"
              value={editCoordinateSpecimen.page || ""}
              disabled={isDetailMode}
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              onChange={(e) => {
                const inputValue = e.target.value;

                setEditCoordinateSpecimen((prev) => ({
                  ...prev,
                  page: inputValue,
                }));
              }}
            />

            {editCoordinateSpecimenWarning.page && (
              <p className="text-xs text-red-500">
                {editCoordinateSpecimenWarning.page}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Lower Left X */}
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Lower Left X *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.lower_left_x ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleCoordinateChange("lower_left_x", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.lower_left_x && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.lower_left_x}
                </p>
              )}
            </div>

            {/* Lower Left Y */}
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Lower Left Y *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.lower_left_y ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleCoordinateChange("lower_left_y", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.lower_left_y && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.lower_left_y}
                </p>
              )}
            </div>

            {/* Upper Right X */}
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Upper Right X *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.upper_right_x ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleCoordinateChange("upper_right_x", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.upper_right_x && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.upper_right_x}
                </p>
              )}
            </div>

            {/* Upper Right Y */}
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Upper Right Y *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.upper_right_y ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleCoordinateChange("upper_right_y", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.upper_right_y && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.upper_right_y}
                </p>
              )}
            </div>
          </div>
        </FormSection>
      )}
      {/* Signature Information */}
      {!isPaperlessMode && (
        <FormSection title="Signature Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Coordinate X *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.textCoordinateX ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleTextCoordinateChange("textCoordinateX", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.textCoordinateX && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.textCoordinateX}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <h4 className="font-medium">Coordinate Y *</h4>

              <input
                type="number"
                min="0"
                value={editCoordinateSpecimen.textCoordinateY ?? ""}
                disabled={isDetailMode}
                onChange={(e) =>
                  handleTextCoordinateChange("textCoordinateY", e.target.value)
                }
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />

              {editCoordinateSpecimenWarning.textCoordinateY && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.textCoordinateY}
                </p>
              )}
            </div>
          </div>
        </FormSection>
      )}
      {/* Hanya untuk mode paperless */}
      {isPaperlessMode && (
        <FormSection title={"Signature"}>
          <div className="flex justify-between items-center">
            {!isDetailMode && (
              <button
                type="button"
                className="text-xs py-2 px-3 rounded-md border border-purple-500 text-purple-600 flex items-center gap-1 hover:bg-purple-50"
                onClick={handleAddCustomQr}
              >
                <FaPlus className="w-3 h-3" />
                Tambah Signature
              </button>
            )}
          </div>

          {qrRedirectCustomList.length === 0 && (
            <p className="text-xs text-gray-500">Belum ada signature</p>
          )}

          {qrRedirectCustomList.map((item, index) => (
            <div
              key={item.id}
              className="rounded-md border border-gray-200 bg-white p-3"
            >
              <div className="flex justify-between items-center mb-3">
                <h5 className="text-sm font-medium text-gray-700">
                  Signature #{index + 1}
                </h5>

                {!isDetailMode && (
                  <button
                    type="button"
                    className="text-red-600 text-xs py-1.5 px-2.5 border border-red-300 rounded-md flex items-center gap-1"
                    onClick={() => handleRemoveCustomQr(item.id)}
                  >
                    <FaTrash className="w-3 h-3" />
                    Hapus
                  </button>
                )}
              </div>

              {isPaperlessMode && (
                <div className="flex flex-col gap-1 max-w-xs mb-2">
                  <label className="text-xs text-gray-600">Type</label>
                  <DropDownListComponent
                    dataSource={QR_REDIRECT_TYPES}
                    fields={{ text: "text", value: "value" }}
                    value={item.type || "qr"}
                    change={(e) =>
                      e.value && handleCustomQrTypeChange(item.id, e.value)
                    }
                    enabled={!isDetailMode}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Page */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Page</label>

                  <input
                    type="number"
                    min="1"
                    max={numPages || 1}
                    value={item.page || ""}
                    disabled={isDetailMode}
                    className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                    onChange={(e) =>
                      handleCustomQrFieldChange(item.id, "page", e.target.value)
                    }
                  />
                </div>

                {/* X */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">X</label>

                  <input
                    type="number"
                    min="0"
                    value={item.x ?? ""}
                    disabled={isDetailMode}
                    className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                    onChange={(e) =>
                      handleCustomQrFieldChange(item.id, "x", e.target.value)
                    }
                  />
                </div>

                {/* Y */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Y</label>
                  <input
                    type="number"
                    min="0"
                    value={item.y !== undefined ? Math.abs(item.y) : ""}
                    disabled={isDetailMode}
                    className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                    onChange={(e) =>
                      handleCustomQrFieldChange(item.id, "y", e.target.value)
                    }
                  />
                </div>

                {/* Size */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Size</label>

                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={item.size ?? ""}
                    disabled={isDetailMode}
                    className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                    onChange={(e) =>
                      handleCustomQrFieldChange(item.id, "size", e.target.value)
                    }
                  />
                </div>
              </div>

              {isPaperlessMode && (
                <div className="mt-3 flex flex-col gap-3">
                  <SubCard title={`Signature Information #${index + 1}`}>
                    <div className="flex flex-col gap-2 items-start">
                      <label className="text-xs text-gray-600">
                        Show Signature Information
                      </label>
                      <input
                        type="checkbox"
                        checked={!!item.showSignatureInfo}
                        disabled={isDetailMode}
                        onChange={() =>
                          handleCustomQrShowSignatureInfoToggle(item.id)
                        }
                        className="p-2 text-sm rounded-[4px]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600">
                          Coordinate X
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={item.signatureInfoX ?? ""}
                          disabled={isDetailMode || !item.showSignatureInfo}
                          className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                          onChange={(e) =>
                            handleCustomQrSignatureInfoFieldChange(
                              item.id,
                              "signatureInfoX",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600">
                          Coordinate Y
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={item.signatureInfoY ?? ""}
                          disabled={isDetailMode || !item.showSignatureInfo}
                          className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                          onChange={(e) =>
                            handleCustomQrSignatureInfoFieldChange(
                              item.id,
                              "signatureInfoY",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  </SubCard>
                </div>
              )}
            </div>
          ))}

          {editCoordinateSpecimenWarning.qrRedirectCustom && (
            <p className="text-xs text-red-500">
              {editCoordinateSpecimenWarning.qrRedirectCustom}
            </p>
          )}
        </FormSection>
      )}
      <FormSection title="QR Redirect">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">QR Redirect Page Mode *</h4>

            <DropDownListComponent
              dataSource={qrPageModes}
              fields={{ text: "text", value: "value" }}
              placeholder="Select Page Mode"
              value={editCoordinateSpecimen.qrRedirectPageMode}
              change={(e) => handleQrRedirectPageModeChange(e.value)}
              enabled={!isDetailMode}
            />
          </div>

          {editCoordinateSpecimen.qrRedirectPageMode === "per-page" && (
            <div className="flex flex-col gap-2">
              <h4 className="font-medium">QR Redirect Page *</h4>

              <input
                type="number"
                min="1"
                max={numPages || 1}
                value={editCoordinateSpecimen.qrRedirectPage || ""}
                disabled={isDetailMode}
                onChange={(e) => handleQrRedirectPageChange(e.target.value)}
                className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* X */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">QR Redirect X *</h4>

            <input
              type="number"
              min="0"
              value={editCoordinateSpecimen.qrRedirectCoordinateX ?? ""}
              disabled={isDetailMode}
              onChange={(e) =>
                handleQrRedirectCoordinateChange(
                  "qrRedirectCoordinateX",
                  e.target.value,
                )
              }
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>

          {/* Y */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">QR Redirect Y *</h4>

            <input
              type="number"
              min="0"
              value={editCoordinateSpecimen.qrRedirectCoordinateY ?? ""}
              disabled={isDetailMode}
              onChange={(e) =>
                handleQrRedirectCoordinateChange(
                  "qrRedirectCoordinateY",
                  e.target.value,
                )
              }
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>

          {/* Size */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">QR Redirect Size *</h4>

            <input
              type="number"
              min="10"
              max="200"
              value={editCoordinateSpecimen.qrRedirectSize ?? ""}
              disabled={isDetailMode}
              onChange={(e) => handleQrRedirectSizeChange(e.target.value)}
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>
        </div>

        {isPaperlessMode &&
          editCoordinateSpecimen.qrRedirectPageMode === "all-page-except" && (
            <SubCard title="Halaman Dikecualikan">
              <p className="text-xs text-gray-500">
                QR Redirect akan tampil di semua halaman, KECUALI halaman yang
                ditambahkan di bawah ini. Bisa lebih dari satu halaman.
              </p>

              {!isDetailMode && (
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="1"
                    max={numPages || 1}
                    placeholder="No. Halaman"
                    value={exceptPageInput}
                    onChange={(e) => setExceptPageInput(e.target.value)}
                    className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500 w-32"
                  />
                  <button
                    type="button"
                    className="text-xs py-2 px-3 rounded-md border border-purple-500 text-purple-600 flex items-center gap-1 hover:bg-purple-50"
                    onClick={handleAddExceptPage}
                  >
                    <FaPlus className="w-3 h-3" />
                    Tambah
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {qrRedirectExceptPages.length === 0 && (
                  <p className="text-xs text-gray-500">
                    Belum ada halaman pengecualian
                  </p>
                )}
                {qrRedirectExceptPages.map((p) => (
                  <span
                    key={p}
                    className="flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full"
                  >
                    Halaman {p}
                    {!isDetailMode && (
                      <FaXmark
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleRemoveExceptPage(p)}
                      />
                    )}
                  </span>
                ))}
              </div>

              {editCoordinateSpecimenWarning.qrRedirectExceptPages && (
                <p className="text-xs text-red-500">
                  {editCoordinateSpecimenWarning.qrRedirectExceptPages}
                </p>
              )}
            </SubCard>
          )}
      </FormSection>
      {editCoordinateSpecimen.qrRedirectPageMode === "per-page" &&
        !isPaperlessMode && (
          <FormSection title={"QR Redirect Custom"}>
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Optional. Tambahkan posisi QR berbeda untuk setiap halaman.
              </p>

              {!isDetailMode && (
                <button
                  type="button"
                  className="text-xs py-2 px-3 rounded-md border border-purple-500 text-purple-600 flex items-center gap-1 hover:bg-purple-50"
                  onClick={handleAddCustomQr}
                >
                  <FaPlus className="w-3 h-3" />
                  Tambah Posisi
                </button>
              )}
            </div>

            {qrRedirectCustomList.length === 0 && (
              <p className="text-xs text-gray-500">Belum ada posisi custom</p>
            )}

            {qrRedirectCustomList.map((item, index) => (
              <div
                key={item.id}
                className="rounded-md border border-gray-200 bg-white p-3"
              >
                <div className="flex justify-between items-center mb-3">
                  <h5 className="text-sm font-medium text-gray-700">
                    Posisi Custom #{index + 1}
                  </h5>

                  {!isDetailMode && (
                    <button
                      type="button"
                      className="text-red-600 text-xs py-1.5 px-2.5 border border-red-300 rounded-md flex items-center gap-1"
                      onClick={() => handleRemoveCustomQr(item.id)}
                    >
                      <FaTrash className="w-3 h-3" />
                      Hapus
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* Page */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-600">Page</label>

                    <input
                      type="number"
                      min="1"
                      max={numPages || 1}
                      value={item.page || ""}
                      disabled={isDetailMode}
                      className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                      onChange={(e) =>
                        handleCustomQrFieldChange(
                          item.id,
                          "page",
                          e.target.value,
                        )
                      }
                    />
                  </div>

                  {/* X */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-600">X</label>

                    <input
                      type="number"
                      min="0"
                      value={item.x ?? ""}
                      disabled={isDetailMode}
                      className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                      onChange={(e) =>
                        handleCustomQrFieldChange(item.id, "x", e.target.value)
                      }
                    />
                  </div>

                  {/* Y */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-600">Y</label>
                    <input
                      type="number"
                      min="0"
                      value={item.y !== undefined ? Math.abs(item.y) : ""}
                      disabled={isDetailMode}
                      className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                      onChange={(e) =>
                        handleCustomQrFieldChange(item.id, "y", e.target.value)
                      }
                    />
                  </div>

                  {/* Size */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-600">Size</label>

                    <input
                      type="number"
                      min="10"
                      max="200"
                      value={item.size ?? ""}
                      disabled={isDetailMode}
                      className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                      onChange={(e) =>
                        handleCustomQrFieldChange(
                          item.id,
                          "size",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            {editCoordinateSpecimenWarning.qrRedirectCustom && (
              <p className="text-xs text-red-500">
                {editCoordinateSpecimenWarning.qrRedirectCustom}
              </p>
            )}
          </FormSection>
        )}
      <FormSection title="Text Info Overlay">
        {/* Show Text Info */}
        <div className="flex flex-col gap-2 items-start">
          <h4 className="font-medium">Show Text Info *</h4>

          <input
            type="checkbox"
            checked={editCoordinateSpecimen.isShowTextInfo}
            onChange={() =>
              setEditCoordinateSpecimen((prev) => ({
                ...prev,
                isShowTextInfo: !prev.isShowTextInfo,
              }))
            }
            disabled={isDetailMode}
            className="p-2 text-sm rounded-[4px]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* X */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Text Info Coordinate X *</h4>

            <input
              type="number"
              min="0"
              value={editCoordinateSpecimen.textInfoCoordinateX ?? ""}
              disabled={isDetailMode}
              onChange={(e) =>
                handleTextInfoCoordinateChange(
                  "textInfoCoordinateX",
                  e.target.value,
                )
              }
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>

          {/* Y */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Text Info Coordinate Y *</h4>

            <input
              type="number"
              min="0"
              value={editCoordinateSpecimen.textInfoCoordinateY ?? ""}
              disabled={isDetailMode}
              onChange={(e) =>
                handleTextInfoCoordinateChange(
                  "textInfoCoordinateY",
                  e.target.value,
                )
              }
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>

          {/* Opacity */}
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Text Info Background Opacity *</h4>

            <input
              type="number"
              min="0"
              max="100"
              value={editCoordinateSpecimen.textInfoBgOpacity ?? ""}
              disabled={isDetailMode}
              onChange={(e) => handleTextInfoBgOpacityChange(e.target.value)}
              className="p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
            />
          </div>
        </div>
      </FormSection>
      <FormSection title="Document Type Selected">
        <div className="flex flex-col md:flex-row gap-2 align-items-start">
          <div className="grow flex flex-col gap-2 items-start">
            <h4 className="font-medium">Orientation</h4>
            {selectedDocumentType?.orientation || "-"}
          </div>
          <div className="grow flex flex-col gap-2 items-start">
            <h4 className="font-medium">Document Type</h4>
            {selectedDocumentType?.text || "-"}
          </div>
        </div>
      </FormSection>
      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Upload File PDF</h4>
        <input
          type="file"
          accept="application/pdf"
          className="flex-grow p-2 text-sm rounded-[4px] border focus:outline-2 border-slate-500 focus:outline-sky-500"
          onChange={onFileChange}
          disabled={isDetailMode}
        />
        {file && (
          <React.Fragment>
            {!isDetailMode && (
              <div className="flex justify-end mt-5">
                <ul className="flex flex-nowrap gap-2 text-sm bg-gray-100 p-1 rounded-md">
                  <li
                    onClick={() => {
                      setIsPreviewPdfMode(true);
                    }}
                    className={`px-4 py-2 rounded-md cursor-pointer ${
                      isPreviewPdfMode
                        ? "bg-sky-400 text-white"
                        : "bg-transparent text-gray-600"
                    }`}
                  >
                    Preview Mode
                  </li>
                  <li
                    onClick={() => {
                      setIsPreviewPdfMode(false);
                    }}
                    className={`px-4 py-2 rounded-md cursor-pointer ${
                      !isPreviewPdfMode
                        ? "bg-sky-400 text-white"
                        : "bg-transparent text-gray-600"
                    }`}
                  >
                    Edit Mode
                  </li>
                </ul>
              </div>
            )}
            <div className="relative mt-2 p-4 border rounded-lg shadow-inner bg-gray-50 max-w-full">
              <div className="max-w-full overflow-x-auto flex flex-col justify-between items-center">
                <Document
                  file={file}
                  onLoadSuccess={onDocumentLoadSuccess}
                  className="w-fit"
                >
                  <div key={"page" + pageNumber} className="relative w-full">
                    <PagePdf
                      pageNumber={pageNumber}
                      canvasRef={pagesRef}
                      onRenderSuccess={() =>
                        setPdfSize({
                          width: pagesRef.current.width,
                          height: pagesRef.current.height,
                        })
                      }
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      className="w-full"
                    />
                    <div className="absolute w-full h-full top-0 left-0">
                      {qrImage &&
                        editCoordinateSpecimen.is_visual_sign &&
                        !isPaperlessMode && (
                          <CustomDraggable
                            qrPosition={qrPosition}
                            setQrPosition={setQrPosition}
                            qrImage={qrImage}
                            qrSize={qrSize}
                            pdfSize={pdfSize}
                            isDetailMode={isDetailMode || isPreviewPdfMode}
                          />
                        )}

                      {/* Hanya tampil dihalaman 1 mode markany */}
                      {!isPaperlessMode && pageNumber == 1 && (
                        <CustomTextDraggable
                          textPosition={textPosition}
                          setTextPosition={setTextPosition}
                          pdfSize={pdfSize}
                          isDetailMode={isDetailMode || isPreviewPdfMode}
                        />
                      )}

                      {editCoordinateSpecimen.isShowTextInfo && (
                        <CustomTextInfoDraggable
                          textPosition={textInfoPosition}
                          setTextPosition={setTextInfoPosition}
                          pdfSize={pdfSize}
                          isDetailMode={isDetailMode || isPreviewPdfMode}
                          bgOpacity={textInfoBgOpacity / 100}
                        />
                      )}

                      {(editCoordinateSpecimen.qrRedirectPageMode ===
                        "all-page" ||
                        (editCoordinateSpecimen.qrRedirectPageMode ===
                          "all-page-except" &&
                          !qrRedirectExceptPages.includes(pageNumber)) ||
                        (editCoordinateSpecimen.qrRedirectPageMode ===
                          "per-page" &&
                          editCoordinateSpecimen.qrRedirectPage ==
                            pageNumber)) &&
                        editCoordinateSpecimen.qrRedirectPageMode !==
                          "none" && (
                          <CustomQrRedirectDraggable
                            textPosition={qrRedirectPosition}
                            setTextPosition={setQrRedirectPosition}
                            qrSize={qrRedirectSize}
                            setQrSize={(size) => setQrRedirectSize(size)}
                            pdfSize={pdfSize}
                            isDetailMode={isDetailMode || isPreviewPdfMode}
                          />
                        )}

                      {(editCoordinateSpecimen.qrRedirectPageMode ===
                        "per-page" ||
                        isPaperlessMode) &&
                        qrRedirectCustomList
                          .filter((item) => Number(item.page) === pageNumber)
                          .map((item) => (
                            <React.Fragment key={item.id}>
                              {isPaperlessMode && item.type === "signature" ? (
                                <CustomSignatureRedirectDraggable
                                  textPosition={{ x: item.x, y: item.y }}
                                  setTextPosition={(position) =>
                                    handleCustomQrPositionChange(
                                      item.id,
                                      position,
                                    )
                                  }
                                  qrSize={item.size}
                                  setQrSize={(size) =>
                                    handleCustomQrSizeChange(item.id, size)
                                  }
                                  qrImage={qrImage}
                                  pdfSize={pdfSize}
                                  isDetailMode={
                                    isDetailMode || isPreviewPdfMode
                                  }
                                />
                              ) : (
                                <CustomQrRedirectDraggable
                                  textPosition={{ x: item.x, y: item.y }}
                                  setTextPosition={(position) =>
                                    handleCustomQrPositionChange(
                                      item.id,
                                      position,
                                    )
                                  }
                                  qrSize={item.size}
                                  setQrSize={(size) =>
                                    handleCustomQrSizeChange(item.id, size)
                                  }
                                  pdfSize={pdfSize}
                                  isDetailMode={
                                    isDetailMode || isPreviewPdfMode
                                  }
                                />
                              )}

                              {isPaperlessMode && item.showSignatureInfo && (
                                <CustomTextDraggable
                                  textPosition={{
                                    x: item.signatureInfoX,
                                    y: -Math.abs(item.signatureInfoY),
                                  }}
                                  setTextPosition={(position) =>
                                    handleCustomQrSignatureInfoPositionChange(
                                      item.id,
                                      position,
                                    )
                                  }
                                  pdfSize={pdfSize}
                                  isDetailMode={
                                    isDetailMode || isPreviewPdfMode
                                  }
                                />
                              )}
                            </React.Fragment>
                          ))}
                      <div className="absolute bottom-1 right-1 text-xl font-semibold text-sky-500 bg-white px-2 rounded-lg">
                        {pageNumber}
                      </div>
                    </div>
                  </div>
                </Document>
              </div>
              <div className="flex justify-between items-center mt-4 bg-white p-4 rounded-md">
                <button
                  className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400"
                  onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                  disabled={pageNumber <= 1}
                >
                  Previous
                </button>
                <div>
                  <p>
                    Page {pageNumber} of {numPages}
                  </p>
                </div>
                <button
                  className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400"
                  onClick={() =>
                    setPageNumber((prev) => Math.min(prev + 1, numPages))
                  }
                  disabled={pageNumber >= numPages}
                >
                  Next
                </button>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Is Active *</h4>
        <DropDownListComponent
          dataSource={[
            { text: "Active", value: true },
            { text: "Inactive", value: false },
          ]}
          fields={{ text: "text", value: "value" }}
          value={editCoordinateSpecimen.is_active}
          change={(e) => {
            handleCoordinateChange("is_active", e.value);
          }}
          enabled={!isDetailMode}
          width="400px"
        />
      </div>
    </div>
  );

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <div className="flex flex-col pb-4">
        <Header title="Signature Specimen Details" />
        <div className="flex justify-between items-end flex-wrap gap-2">
          <button
            style={{ backgroundColor: currentColor }}
            className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
            onClick={() => navigate("/markany/signature-specimen")}
          >
            <FaArrowLeft className="w-3 h-3" />
            <div className="text-nowrap">Back to List</div>
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              style={{ backgroundColor: currentColor }}
              className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
              onClick={handleSync}
              disabled={loading}
            >
              <FaSync className="w-3 h-3" />
              <div className="text-nowrap">
                {loading && loadingAction === "sync"
                  ? "Syncing..."
                  : "Sync Specimen Coordinate"}
              </div>
            </button>
            <button
              style={{ backgroundColor: currentColor }}
              className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
              onClick={() => handleAdd()}
              disabled={loading}
            >
              <FaPlus className="w-3 h-3" />
              <div className="text-nowrap">Add Coordinate Signature</div>
            </button>
          </div>
        </div>
      </div>

      {specimenDetail && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            Specimen Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Integrated Platform</p>
              <p className="font-medium text-gray-900">
                {specimenDetail.integrated_member?.integrated_platform
                  ?.platform_name || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Integrated Member</p>
              <p className="font-medium text-gray-900">
                {specimenDetail.integrated_member?.user?.name || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Type TTD</p>
              <p className="font-medium text-gray-900 capitalize">
                {specimenDetail.type_ttd || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Specimen</p>
              {specimenDetail.filepath && (
                <img
                  src={`data:image/*;base64,${specimenDetail.filepath}`}
                  alt="Specimen"
                  className="w-48 object-cover border rounded"
                />
              )}
              <p
                className="text-gray-500 text-sm mt-1"
                style={{ display: "none" }}
              >
                Image not available
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-fit cursor-pointer">
          <GridComponent
            dataSource={coordinateSpecimenList}
            allowPaging
            allowSorting
            allowTextWrap={true}
            pageSettings={{
              pageCount: 5,
              pageSizes: ["All", "10", "25", "50"],
            }}
            textWrapSettings={{ wrapMode: "Content" }}
            toolbar={["Search", "ExcelExport"]}
            queryCellInfo={queryCellInfo}
            allowExcelExport={true}
            toolbarClick={toolbarClick}
            dataBound={dataBound}
            ref={gridRef}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="No"
                headerText="No"
                textAlign="Center"
                width={50}
                type="Number"
              />
              <ColumnDirective
                field="Uuid"
                headerText="Action"
                textAlign="Center"
                isPrimaryKey={true}
                template={actionTemplate}
                minWidth="250"
              />
              <ColumnDirective
                field="VarLocation"
                headerText="Var Location"
                minWidth="150"
              />
              <ColumnDirective
                field="VarReason"
                headerText="Var Reason"
                minWidth="150"
              />
              <ColumnDirective
                field="MembershipName"
                headerText="Membership Type"
              />
              <ColumnDirective
                field="DocType"
                headerText="Document Type"
                minWidth="150"
              />
              <ColumnDirective
                field="KeyDocType"
                headerText="Key Document Type"
                minWidth="150"
              />
              <ColumnDirective
                field="CertificateLevel"
                headerText="Certificate Level"
                minWidth="150"
              />
              <ColumnDirective
                field="SpecimenMode"
                headerText="Mode"
                textAlign="Center"
                width={110}
                template={(props) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      props.SpecimenMode === "paperless"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {props.SpecimenMode}
                  </span>
                )}
              />
              <ColumnDirective
                field="CreatedAt"
                headerText="Created At"
                minWidth="200"
              />
              <ColumnDirective
                field="IsActive"
                headerText="Is Active"
                textAlign="Center"
                width={120}
                template={(props) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      props.IsActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {props.IsActive ? "Active" : "Inactive"}
                  </span>
                )}
              />
            </ColumnsDirective>
            <Inject
              services={[Search, Toolbar, Page, Sort, Resize, ExcelExport]}
            />
          </GridComponent>
        </div>
      </div>

      {toggleAdd && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium text-nowrap">
                Add Coordinate Signature
              </h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (loading ? "cursor-not-allowed opacity-50" : "cursor-pointer")
                }
                onClick={() => handleSubmit(false)}
              >
                {!loading ? "Add Coordinate" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleEdit && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium text-nowrap">
                Edit Coordinate Signature
              </h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (loading ? "cursor-not-allowed opacity-50" : "cursor-pointer")
                }
                onClick={() => handleSubmit(true)}
              >
                {!loading ? "Edit Coordinate" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleDetail && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">
                Coordinate Specimen Details
              </h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center items-center w-full min-h-screen h-full overflow-y-auto py-10 px-4 bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-4xl h-fit rounded-xl p-8">
            <div>
              {loadingAction === "add"
                ? "Adding Coordinate Signature. Please wait..."
                : loadingAction === "edit"
                  ? "Updating Coordinate Signature. Please wait..."
                  : loadingAction === "delete"
                    ? "Deleting Coordinate Signature. Please wait..."
                    : loadingAction === "upload"
                      ? "Uploading PDF to Markany. Please wait..."
                      : loadingAction === "sync"
                        ? "Syncing Specimen Coordinate. Please wait..."
                        : "Processing. Please wait..."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureSpecimenDetail;
